import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON) {
  throw new Error('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY in .env.local');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// ── AUTH ──────────────────────────────────────────────────────────────────────

export async function signUp({ email, password, fullName, role = 'member' }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName, role } },
  });
  if (error) throw error;
  return data;
}

export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

// ── BLOCKS ────────────────────────────────────────────────────────────────────

export async function fetchBlocks(coachId) {
  const { data, error } = await supabase
    .from('blocks')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function fetchAllBlocks() {
  // Members fetch all blocks (RLS ensures they're authenticated)
  const { data, error } = await supabase
    .from('blocks')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function upsertBlock(block) {
  const { data, error } = await supabase
    .from('blocks')
    .upsert(block, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBlock(blockId) {
  const { error } = await supabase.from('blocks').delete().eq('id', blockId);
  if (error) throw error;
}

// ── WORKOUT LOGS ─────────────────────────────────────────────────────────────

export async function fetchMyLogs(memberId) {
  const { data, error } = await supabase
    .from('workout_logs')
    .select('*')
    .eq('member_id', memberId)
    .order('log_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function fetchAllLogs() {
  // Coach fetches everyone's logs
  const { data, error } = await supabase
    .from('workout_logs')
    .select('*, profiles(full_name, email)')
    .order('log_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function insertLog(log) {
  const { data, error } = await supabase
    .from('workout_logs')
    .insert(log)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── COACH CALENDAR ───────────────────────────────────────────────────────────

export async function fetchCalendarEntries(coachId) {
  const { data, error } = await supabase
    .from('coach_calendar')
    .select('*')
    .eq('coach_id', coachId)
    .order('date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function upsertCalendarEntry(entry) {
  const { data, error } = await supabase
    .from('coach_calendar')
    .upsert(entry, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCalendarEntry(id) {
  const { error } = await supabase
    .from('coach_calendar')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── MEMBERS (coach view) ──────────────────────────────────────────────────────

export async function fetchMembers() {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('role', 'member')
    .order('full_name');
  if (error) throw error;
  return data;
}

// ── CHECK-INS ─────────────────────────────────────────────────────────────────

export async function fetchMyCheckins(memberId) {
  const { data, error } = await supabase
    .from('checkins')
    .select('*')
    .eq('member_id', memberId);
  if (error) throw error;
  return data;
}

export async function fetchCheckinsForSession(sessionId, date) {
  const { data, error } = await supabase
    .from('checkins')
    .select('*, profiles(full_name, email)')
    .eq('session_id', sessionId)
    .eq('checkin_date', date);
  if (error) throw error;
  return data;
}

export async function addCheckin({ memberId, sessionId, blockId }) {
  const { data, error } = await supabase
    .from('checkins')
    .upsert({
      member_id: memberId,
      session_id: sessionId,
      block_id: blockId,
      checkin_date: new Date().toISOString().split('T')[0],
    }, { onConflict: 'member_id,session_id,checkin_date' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function addCheckinMemberSession({ memberId, date, title, type, blockSections }) {
  // Skip if a planned/completed session already exists for this member+date+title
  const { data: existing } = await supabase
    .from('member_sessions')
    .select('id')
    .eq('member_id', memberId)
    .eq('date', date)
    .eq('title', title)
    .maybeSingle();
  if (existing) return existing;

  // Convert coach block sections → member_sessions blocks format
  const blocks = (blockSections || []).map(sec => ({
    id: sec.id,
    kind: 'exercise',
    group: sec.name || 'Main',
    flowMode: sec.flowMode || 'circuit',
    timing: sec.timing || '',
    notes: sec.notes || '',
    exercises: (sec.exercises || []).map(ex => ({
      id: ex.id,
      name: ex.name,
      sets: ex.sets || '',
      reps: ex.reps || '',
      rx: ex.rx || '',
      tempo: '',
      rest: '',
    })),
  }));

  const { data, error } = await supabase
    .from('member_sessions')
    .insert({
      member_id: memberId,
      date,
      title,
      type: type || 'Strength',
      notes: '',
      status: 'planned',
      sections: JSON.stringify({ warmup: '', cooldown: '', blocks }),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function removeCheckin({ memberId, sessionId }) {
  const date = new Date().toISOString().split('T')[0];
  const { error } = await supabase
    .from('checkins')
    .delete()
    .eq('member_id', memberId)
    .eq('session_id', sessionId)
    .eq('checkin_date', date);
  if (error) throw error;
}

// ── TEST RESULTS ──────────────────────────────────────────────────────────────

export async function fetchMyTestResults(memberId) {
  const { data, error } = await supabase
    .from('test_results')
    .select('*')
    .eq('member_id', memberId)
    .order('test_date', { ascending: true });
  if (error) throw error;
  return data;
}

export async function upsertTestResult({ memberId, testId, testDate, scores }) {
  const { data, error } = await supabase
    .from('test_results')
    .upsert({
      member_id: memberId,
      test_id: testId,
      test_date: testDate,
      scores,
    }, { onConflict: 'member_id,test_id,test_date' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchAllTestResults() {
  const { data, error } = await supabase
    .from('test_results')
    .select('*, profiles(full_name, email)')
    .order('test_date', { ascending: true });
  if (error) throw error;
  return data;
}

// ── MEMBER PROFILE ────────────────────────────────────────────────────────────

export async function saveHealthAndSafety({ userId, emergencyContactName, emergencyContactNumber, emergencyContactRelationship, parqAnswers }) {
  const { data, error } = await supabase
    .from('profiles')
    .update({
      emergency_contact_name: emergencyContactName,
      emergency_contact_number: emergencyContactNumber,
      emergency_contact_relationship: emergencyContactRelationship,
      parq_answers: parqAnswers,
      parq_completed: true,
      parq_completed_at: new Date().toISOString(),
    })
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function sendPasswordReset(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

export async function fetchMemberTestResults(memberId) {
  const { data, error } = await supabase
    .from('test_results')
    .select('*')
    .eq('member_id', memberId)
    .order('test_date', { ascending: true });
  if (error) throw error;
  return data;
}

export async function fetchMemberTestScores(memberId) {
  const { data, error } = await supabase
    .from('member_test_results')
    .select('*')
    .eq('member_id', memberId)
    .order('tested_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function insertMemberTestScore(record) {
  const { data, error } = await supabase
    .from('member_test_results')
    .insert(record)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMemberTestScore(id) {
  const { error } = await supabase
    .from('member_test_results')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function deleteMember(memberId) {
  // Delete all member data across tables — profile delete cascades via FK
  await supabase.from('workout_logs').delete().eq('member_id', memberId);
  await supabase.from('test_results').delete().eq('member_id', memberId);
  await supabase.from('checkins').delete().eq('member_id', memberId);
  await supabase.from('profiles').delete().eq('id', memberId);
  // Note: auth.users deletion requires service role key — profile delete is sufficient
  // to block login since the app checks profiles on auth
}

export async function updateMemberTags(memberId, tags) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ tags })
    .eq('id', memberId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateMemberTier(memberId, tier) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ subscription_tier: tier })
    .eq('id', memberId)
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

// ── MEMBER SESSIONS (Personal Calendar) ─────────────────────────────────────

export async function fetchMemberSessions(memberId) {
  const { data, error } = await supabase
    .from('member_sessions')
    .select('*')
    .eq('member_id', memberId)
    .order('date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchAllMemberSessions() {
  const { data, error } = await supabase
    .from('member_sessions')
    .select('*')
    .order('date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function upsertMemberSession(session) {
  const { data, error } = await supabase
    .from('member_sessions')
    .upsert(session, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMemberSession(sessionId) {
  const { error } = await supabase
    .from('member_sessions')
    .delete()
    .eq('id', sessionId);
  if (error) throw error;
}

export async function fetchMemberSessionsRecent(memberId, cutoffDate) {
  const { data, error } = await supabase
    .from('member_sessions')
    .select('date, title, type, status, sections')
    .eq('member_id', memberId)
    .gte('date', cutoffDate)
    .order('date', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchMemberLogsRecent(memberId, cutoffDate) {
  const { data, error } = await supabase
    .from('workout_logs')
    .select('log_date, session_title, session_type, log_data')
    .eq('member_id', memberId)
    .gte('log_date', cutoffDate)
    .order('log_date', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ── COACH NOTES ───────────────────────────────────────────────────────────────

export async function fetchCoachNotes(memberId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('coach_notes')
    .eq('id', memberId)
    .single();
  if (error) return '';
  return data?.coach_notes || '';
}

export async function saveCoachNotes(memberId, notes) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ coach_notes: notes })
    .eq('id', memberId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── MEMBER PLANS & PHASES ─────────────────────────────────────────────────────

export async function fetchMemberPlan(memberId) {
  const { data } = await supabase
    .from('member_plans')
    .select('*')
    .eq('member_id', memberId)
    .maybeSingle();
  return data || null;
}

export async function upsertMemberPlan(plan) {
  const { data, error } = await supabase
    .from('member_plans')
    .upsert(plan, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchMemberPhases(planId) {
  const { data, error } = await supabase
    .from('member_phases')
    .select('*')
    .eq('plan_id', planId)
    .order('start_date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function upsertMemberPhase(phase) {
  const { data, error } = await supabase
    .from('member_phases')
    .upsert(phase, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMemberPhase(phaseId) {
  const { error } = await supabase
    .from('member_phases')
    .delete()
    .eq('id', phaseId);
  if (error) throw error;
}

export async function fetchMyPhases(memberId) {
  const plan = await fetchMemberPlan(memberId);
  if (!plan) return [];
  return fetchMemberPhases(plan.id);
}

// ── PROGRAMME LIBRARY ─────────────────────────────────────────────────────────

export async function fetchProgrammeLibrary(coachId) {
  const { data, error } = await supabase
    .from('programme_library')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchPublishedProgrammes() {
  const { data, error } = await supabase
    .from('programme_library')
    .select('*')
    .eq('is_published', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function upsertProgrammeLibraryRow(row) {
  const { data, error } = await supabase
    .from('programme_library')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProgrammeLibraryRow(id) {
  const { error } = await supabase
    .from('programme_library')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── EXERCISE LIBRARY ─────────────────────────────────────────────────────────

export async function fetchExercises(coachId) {
  const { data, error } = await supabase
    .from('exercises')
    .select('*')
    .eq('coach_id', coachId)
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function searchExercises(term) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('exercises')
    .select('id, name, youtube_url')
    .eq('coach_id', user.id)
    .ilike('name', `%${term}%`)
    .limit(10);
  if (error) throw error;
  return data || [];
}

export async function upsertExercise(exercise) {
  const { data, error } = await supabase
    .from('exercises')
    .upsert(exercise, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteExercise(id) {
  const { error } = await supabase.from('exercises').delete().eq('id', id);
  if (error) throw error;
}

// ── MEMBER PROGRAMMES ─────────────────────────────────────────────────────────

export async function fetchMemberProgrammes(memberId) {
  try {
    const { data, error } = await supabase
      .from('member_programmes')
      .select('programme_id')
      .eq('member_id', memberId)
      .eq('status', 'active');
    if (error) return [];
    return data || [];
  } catch { return []; }
}

export async function fetchActiveMemberProgrammeWithDetails(memberId) {
  try {
    const { data, error } = await supabase
      .from('member_programmes')
      .select('id, programme_id, start_date, days_per_week, programme_library(id, title, cover_image_url, weeks_data, category)')
      .eq('member_id', memberId)
      .eq('status', 'active')
      .maybeSingle();
    if (error) return null;
    return data || null;
  } catch { return null; }
}

export async function upsertMemberProgramme(row) {
  const { data: existing, error: fetchError } = await supabase
    .from('member_programmes')
    .select('id')
    .eq('member_id', row.member_id)
    .eq('programme_id', row.programme_id)
    .maybeSingle();
  if (fetchError) throw fetchError;

  if (existing) {
    const { data, error } = await supabase
      .from('member_programmes')
      .update({ status: row.status, days_per_week: row.days_per_week, start_date: row.start_date })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('member_programmes')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return data;
  }
}

export async function unenrolMemberFromProgramme(memberId, programmeId) {
  const { error: sessError } = await supabase
    .from('member_sessions')
    .delete()
    .eq('member_id', memberId)
    .eq('programme_id', programmeId)
    .neq('status', 'completed');
  if (sessError) throw sessError;
  const { error: progError } = await supabase
    .from('member_programmes')
    .delete()
    .eq('member_id', memberId)
    .eq('programme_id', programmeId);
  if (progError) throw progError;
}

export async function clearMemberSessions(memberId) {
  const { error: sessError } = await supabase
    .from('member_sessions')
    .delete()
    .eq('member_id', memberId);
  if (sessError) throw sessError;
  const { error: progError } = await supabase
    .from('member_programmes')
    .delete()
    .eq('member_id', memberId);
  if (progError) throw progError;
}

export async function fetchProgrammeEnrolmentCounts(programmeIds) {
  if (!programmeIds.length) return {};
  const { data, error } = await supabase
    .from('member_programmes')
    .select('programme_id')
    .in('programme_id', programmeIds)
    .eq('status', 'active');
  if (error) return {};
  const counts = {};
  (data || []).forEach(row => {
    counts[row.programme_id] = (counts[row.programme_id] || 0) + 1;
  });
  return counts;
}

export async function fetchProgrammeEnrolments(programmeId) {
  const { data, error } = await supabase
    .from('member_programmes')
    .select('id, member_id, start_date, days_per_week, profiles(full_name, email)')
    .eq('programme_id', programmeId)
    .eq('status', 'active')
    .order('start_date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function unenrolMember(enrolmentId, memberId) {
  const { error: updateError } = await supabase
    .from('member_programmes')
    .update({ status: 'cancelled' })
    .eq('id', enrolmentId);
  if (updateError) throw updateError;
  const today = new Date().toISOString().slice(0, 10);
  const { error: deleteError } = await supabase
    .from('member_sessions')
    .delete()
    .eq('member_id', memberId)
    .eq('status', 'planned')
    .gte('date', today);
  if (deleteError) throw deleteError;
}

export async function bulkInsertMemberSessions(sessions) {
  const { data, error } = await supabase
    .from('member_sessions')
    .insert(sessions)
    .select();
  if (error) throw error;
  return data || [];
}

export async function uploadProgrammeCover(file) {
  const ext = file.name.split('.').pop();
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}.${ext}`;
  const { error } = await supabase.storage
    .from('programme-covers')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('programme-covers').getPublicUrl(path);
  return data.publicUrl;
}

// ── COMMUNITY ANNOUNCEMENTS ───────────────────────────────────────────────────

export async function fetchAnnouncements() {
  const { data, error } = await supabase
    .from('community_announcements')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function insertAnnouncement({ coach_id, title, body }) {
  const { data, error } = await supabase
    .from('community_announcements')
    .insert({ coach_id, title, body })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateAnnouncement(id, { title, body }) {
  const { data, error } = await supabase
    .from('community_announcements')
    .update({ title, body })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteAnnouncement(id) {
  const { error } = await supabase
    .from('community_announcements')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── COMMUNITY CHAT ────────────────────────────────────────────────────────────

export async function fetchMessages(channel) {
  const { data, error } = await supabase
    .from('community_messages')
    .select('*')
    .eq('channel', channel)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchLastMessages() {
  const channels = ['all', 'class', 'coaching'];
  const cutoff = new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString();
  const lastMsg = {};
  const hasRecent = {};
  await Promise.all(channels.map(async ch => {
    const { data } = await supabase
      .from('community_messages')
      .select('body, display_name, created_at')
      .eq('channel', ch)
      .order('created_at', { ascending: false })
      .limit(1);
    const msg = data?.[0] || null;
    lastMsg[ch] = msg;
    hasRecent[ch] = msg ? msg.created_at >= cutoff : false;
  }));
  return { lastMsg, hasRecent };
}

export async function fetchChannelMemberCounts() {
  const { data, error } = await supabase
    .from('profiles')
    .select('tags')
    .eq('role', 'member');
  if (error) return { all: 0, class: 0, coaching: 0 };
  const members = data || [];
  return {
    all:      members.length,
    class:    members.filter(m => (m.tags || []).some(t => t.toLowerCase() === 'class')).length,
    coaching: members.filter(m => (m.tags || []).some(t => ['1-1', 'online'].includes(t.toLowerCase()))).length,
  };
}

export async function sendChatMessage({ user_id, display_name, body, is_coach, channel }) {
  const { error } = await supabase
    .from('community_messages')
    .insert({ user_id, display_name, body, is_coach, channel });
  if (error) {
    console.error('[sendChatMessage] Supabase error:', error);
    throw error;
  }
}

export async function deleteChatMessage(id) {
  const { error } = await supabase
    .from('community_messages')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function fetchReactions(messageIds) {
  if (!messageIds.length) return {};
  const { data, error } = await supabase
    .from('community_reactions')
    .select('message_id, user_id, emoji')
    .in('message_id', messageIds);
  if (error) throw error;
  const result = {};
  (data || []).forEach(({ message_id, user_id, emoji }) => {
    if (!result[message_id]) result[message_id] = {};
    if (!result[message_id][emoji]) result[message_id][emoji] = [];
    result[message_id][emoji].push(user_id);
  });
  return result;
}

export async function toggleReaction({ message_id, user_id, emoji, hasReacted }) {
  if (hasReacted) {
    const { error } = await supabase
      .from('community_reactions')
      .delete()
      .eq('message_id', message_id)
      .eq('user_id', user_id)
      .eq('emoji', emoji);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('community_reactions')
      .insert({ message_id, user_id, emoji });
    if (error) throw error;
  }
}

// ── COMMUNITY RESOURCES ───────────────────────────────────────────────────────

export async function fetchResources() {
  const { data, error } = await supabase
    .from('community_resources')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function upsertResource(resource) {
  const { data, error } = await supabase
    .from('community_resources')
    .upsert(resource)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteResource(id) {
  const { error } = await supabase
    .from('community_resources')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function updateResourceSortOrder(id, sort_order) {
  const { error } = await supabase
    .from('community_resources')
    .update({ sort_order })
    .eq('id', id);
  if (error) throw error;
}

export async function uploadResourceFile(file) {
  const ext  = file.name.split('.').pop();
  const path = `resources/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage
    .from('community-resources')
    .upload(path, file, { upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from('community-resources').getPublicUrl(path);
  return data.publicUrl;
}

export async function uploadShopImage(file) {
  const ext  = file.name.split('.').pop();
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage
    .from('shop-images')
    .upload(path, file, { upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from('shop-images').getPublicUrl(path);
  return data.publicUrl;
}

// ── PACKAGE MANAGER ──────────────────────────────────────────────────────────

export async function fetchPackages(coachId) {
  const { data, error } = await supabase
    .from('class_packages')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function upsertPackage(pkg) {
  const { data, error } = await supabase
    .from('class_packages')
    .upsert(pkg, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchActivePackages() {
  const { data, error } = await supabase
    .from('class_packages')
    .select('*')
    .eq('is_active', true)
    .not('stripe_price_id', 'is', null)
    .order('price', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchPackageBundles(packageId) {
  const { data, error } = await supabase
    .from('member_token_bundles')
    .select('*')
    .eq('package_id', packageId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchMemberBundles(memberId) {
  const { data, error } = await supabase
    .from('member_token_bundles')
    .select('*, class_packages(id,name)')
    .eq('member_id', memberId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function insertBundle(bundle) {
  const { data, error } = await supabase
    .from('member_token_bundles')
    .insert(bundle)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateBundle(id, changes) {
  const { data, error } = await supabase
    .from('member_token_bundles')
    .update(changes)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function insertTokenTransaction(tx) {
  const { data, error } = await supabase
    .from('token_transactions')
    .insert(tx)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchBundleTransactions(bundleId) {
  const { data, error } = await supabase
    .from('token_transactions')
    .select('*')
    .eq('bundle_id', bundleId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchAllMemberBundles(coachId) {
  const { data, error } = await supabase
    .from('member_token_bundles')
    .select('*, class_packages(id,name)')
    .eq('coach_id', coachId)
    .gt('tokens_remaining', 0);
  if (error) throw error;
  return data || [];
}

// ── CLASS BOOKINGS ────────────────────────────────────────────────────────────

export async function fetchClassBookings(sessionId) {
  const { data, error } = await supabase
    .from('class_bookings')
    .select('*, profiles(id, full_name, email)')
    .eq('class_session_id', sessionId)
    .in('status', ['booked', 'waitlist'])
    .order('booked_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchMemberBookings(memberId) {
  const { data, error } = await supabase
    .from('class_bookings')
    .select('*')
    .eq('member_id', memberId)
    .in('status', ['booked', 'waitlist'])
    .order('booked_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function insertClassBooking(booking) {
  const { data, error } = await supabase
    .from('class_bookings')
    .insert(booking)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateClassBooking(id, changes) {
  const { data, error } = await supabase
    .from('class_bookings')
    .update(changes)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchBookingsForSessions(sessionIds) {
  if (!sessionIds.length) return [];
  const { data, error } = await supabase
    .from('class_bookings')
    .select('*, profiles(id, full_name, email)')
    .in('class_session_id', sessionIds)
    .in('status', ['booked', 'waitlist'])
    .order('booked_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ── SHOP ─────────────────────────────────────────────────────────────────────

export async function fetchShopProducts() {
  const { data, error } = await supabase
    .from('shop_products')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchActiveShopProducts() {
  const { data, error } = await supabase
    .from('shop_products')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function upsertShopProduct(product) {
  const { data, error } = await supabase
    .from('shop_products')
    .upsert(product, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchShopOrders() {
  const { data, error } = await supabase
    .from('shop_orders')
    .select('*, profiles(full_name, email)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateShopOrderStatus(id, status) {
  const { data, error } = await supabase
    .from('shop_orders')
    .update({ status })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function insertShopOrder(order) {
  const { data, error } = await supabase
    .from('shop_orders')
    .insert(order)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchMyShopOrders(memberId) {
  const { data, error } = await supabase
    .from('shop_orders')
    .select('*')
    .eq('member_id', memberId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function insertShopOrders(orders) {
  const { data, error } = await supabase.from('shop_orders').insert(orders).select();
  if (error) throw error;
  return data || [];
}

export async function updateOrderGroupStatus(orderRef, status) {
  const { error } = await supabase.from('shop_orders').update({ status }).eq('order_reference', orderRef);
  if (error) throw error;
}

export async function updateShopOrderItem(id, updates) {
  const { data, error } = await supabase.from('shop_orders').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return data;
}
